import { en } from './locales/en';
import { localeState } from '../stores/locale.store';
import { DEFAULT_LOCALE } from '../config/constants';

/**
 * i18n core: locale resolution, message lookup (`t`) and date formatting.
 *
 * How it works:
 * - Every locale dictionary mirrors the English one (see locales/en/index.ts).
 * - `t()` reads the active dictionary from the locale store, so any component
 *   that calls `t()` inside render() automatically re-renders when the
 *   locale changes (@stencil/store reactivity).
 * - Missing keys fall back to English, and finally to the key itself, so the
 *   UI never renders a blank string.
 */

/** Locales the app ships dictionaries for. */
export type SupportedLocale = 'en' | 'pt-PT' | 'pt-BR' | 'es';

/** All supported locales, used for matching user/browser language tags. */
export const SUPPORTED_LOCALES: readonly SupportedLocale[] = ['en', 'pt-PT', 'pt-BR', 'es'];

/**
 * Default regional variant for a bare language tag.
 * Example: a user with language "pt" (or an unsupported region like "pt-MZ")
 * gets European Portuguese, per product decision (Niup's primary market).
 */
const REGION_DEFAULTS: Record<string, SupportedLocale> = {
  pt: 'pt-PT',
  en: 'en',
  es: 'es',
};

/**
 * Normalize a language tag to BCP-47-like casing.
 * Accepts OAuth-style underscores too: "pt_br" → "pt-BR".
 */
function normalizeTag(raw: string): string {
  const [lang, region] = raw.trim().replace('_', '-').split('-');
  return region ? `${lang.toLowerCase()}-${region.toUpperCase()}` : lang.toLowerCase();
}

/**
 * Pick the best supported locale from an ordered list of candidates
 * (e.g. [localStorage override, profile.languageCode, ...navigator.languages]).
 *
 * For each candidate: exact match first ("pt-BR" → pt-BR), then base-language
 * match ("pt-MZ" → pt-PT, "es-AR" → es, "en-GB" → en). If no candidate
 * matches, falls back to English.
 */
export function resolveLocale(candidates: Array<string | null | undefined>): SupportedLocale {
  for (const raw of candidates) {
    if (!raw) continue;
    const tag = normalizeTag(raw);

    const exact = SUPPORTED_LOCALES.find((locale) => locale.toLowerCase() === tag.toLowerCase());
    if (exact) return exact;

    const base = tag.split('-')[0];
    if (REGION_DEFAULTS[base]) return REGION_DEFAULTS[base];
  }
  return DEFAULT_LOCALE;
}

/** Values injected into `{placeholder}` slots of a message. */
export type MessageParams = Record<string, string | number>;

/** A leaf in a dictionary: plain string or plural forms keyed by CLDR category. */
type MessageNode = string | { [category: string]: string };

/** Walk a dot-separated key ("profile.header.editButton") through a dictionary. */
function lookup(dict: unknown, key: string): MessageNode | undefined {
  let node: unknown = dict;
  for (const part of key.split('.')) {
    if (node == null || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === 'string' || (node != null && typeof node === 'object') ? (node as MessageNode) : undefined;
}

/** Replace `{name}` placeholders with values from params (unknown ones are kept as-is). */
function interpolate(template: string, params?: MessageParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name) => (params[name] != null ? String(params[name]) : match));
}

/**
 * Translate a message key in the active locale.
 *
 * - Plain keys: `t('common.actions.save')` → "Save"
 * - Interpolation: `t('profile.greeting', { name: 'Ana' })` with "Hello {name}"
 * - Plurals: pass `count` and define the key as a plural object
 *   `{ one: '{count} request', other: '{count} requests' }` — the right form
 *   is picked via Intl.PluralRules for the active locale.
 *
 * IMPORTANT: call `t()` inside render() (or helpers used by render), never
 * cache its result in class fields — otherwise the text won't follow the
 * locale and the component won't subscribe to locale changes.
 */
export function t(key: string, params?: MessageParams): string {
  // Reading localeState.messages here is what subscribes the calling
  // component to locale changes.
  const node = lookup(localeState.messages, key) ?? lookup(en, key);

  if (node == null) {
    console.warn(`[i18n] Missing translation key: "${key}"`);
    return key;
  }

  let template: string;
  if (typeof node === 'string') {
    template = node;
  } else {
    // Plural object: choose the CLDR category for the given count.
    const count = typeof params?.count === 'number' ? params.count : Number(params?.count ?? NaN);
    const category = Number.isNaN(count) ? 'other' : new Intl.PluralRules(localeState.locale).select(count);
    template = node[category] ?? node.other ?? key;
  }

  return interpolate(template, params);
}

/**
 * Format a date in the active locale.
 * Defaults to a long date (e.g. "March 1, 2026" / "1 de março de 2026").
 */
export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const value = typeof date === 'string' ? new Date(date) : date;
  const opts = options ?? { year: 'numeric', month: 'long', day: 'numeric' };
  return new Intl.DateTimeFormat(localeState.locale, opts).format(value);
}
